import type {
  AccessGrantDiff,
  AccessResourceType,
} from '~/features/access-control/types/AccessControl';

import { updateMockResourceAccess } from '~/features/access-control/mocks/accessControlMockStore';
import { showAccessControlSuccessToast } from '~/features/access-control/mocks/showAccessControlSuccessToast';
import { withAccessControlMockFallback } from '~/features/access-control/mocks/withAccessControlMockFallback';
import { ServiceRequestConfig } from '~/services/createService';
import { pilot } from '~/services/serviceInstances';

import type {
  ResourceAccess,
  ServerAccessGrant,
  ServerAccessPrincipalRef,
  ServerResourceAccess,
} from './accessGrantMapper';

import {
  mapGrantToServer,
  mapPrincipalToServerRef,
  mapResourceAccessResponse,
} from './accessGrantMapper';
import { isClauseLibraryAccessResourceType } from './clauseAccessMapper';

type Request = {
  resourceId: string;
  resourceType: AccessResourceType;
} & AccessGrantDiff;

type ServerRequest = {
  resource_id: string;
  resource_type: AccessResourceType;
  inherit_from_parent: boolean;
  added_grants: ServerAccessGrant[];
  updated_grants: ServerAccessGrant[];
  removed_principals: ServerAccessPrincipalRef[];
};

export const updateResourceAccess = async (
  request: Request,
  options: ServiceRequestConfig = {},
): Promise<ResourceAccess> => {
  if (isClauseLibraryAccessResourceType(request.resourceType)) {
    throw new Error(
      `updateResourceAccess does not support resourceType "${request.resourceType}"; use updateClauseResourceAccess`,
    );
  }

  return withAccessControlMockFallback(
    async () => {
      const serverResponse = await pilot.put<ServerResourceAccess>(
        '/access/resource/',
        mapRequest(request),
        options,
      );

      return mapResourceAccessResponse(serverResponse);
    },
    () => {
      const result = updateMockResourceAccess(
        request.resourceType,
        request.resourceId,
        {
          inheritFromParent: request.inheritFromParent,
          addedGrants: request.addedGrants,
          updatedGrants: request.updatedGrants,
          removedPrincipalKeys: request.removedPrincipals.map(
            (principal) => `${principal.type}:${principal.id}`,
          ),
        },
      );

      showAccessControlSuccessToast(options);

      return result;
    },
  );
};

function mapRequest(request: Request): ServerRequest {
  return {
    resource_id: request.resourceId,
    resource_type: request.resourceType,
    inherit_from_parent: request.inheritFromParent,
    added_grants: request.addedGrants.map(mapGrantToServer),
    updated_grants: request.updatedGrants.map(mapGrantToServer),
    removed_principals: request.removedPrincipals.map(mapPrincipalToServerRef),
  };
}
